import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useDeviceDetection } from '../../hooks/useDeviceDetection';
import { useAuth } from '../../hooks/useAuth';
import { Role } from '../../types';

/** Écran de chargement plein écran (évite la page blanche/noire sur mobile après splash) */
const MobileLoadingScreen: React.FC = () => (
  <div className="min-h-screen w-full flex items-center justify-center bg-gray-100 dark:bg-gray-950" aria-hidden="true">
    <div className="w-10 h-10 border-2 border-primary-500 dark:border-primary-400 border-t-transparent rounded-full animate-spin" />
  </div>
);

/**
 * Route garde pour l'application mobile uniquement
 * - Bloque l'accès desktop
 * - Redirige vers login si non authentifié
 * - Redirige vers le dashboard approprié selon le rôle
 * - Affiche un écran de chargement au lieu de null pour éviter la page blanche/noire
 */
export const MobileOnlyRoute: React.FC = () => {
  const { isMobile } = useDeviceDetection();
  const { user, loading } = useAuth();

  // Si on est sur desktop, rediriger vers la page d'accueil
  if (!isMobile) {
    return <Navigate to="/" replace />;
  }

  // Attendre le chargement de l'authentification (ne jamais retourner null → page blanche)
  if (loading) {
    return <MobileLoadingScreen />;
  }

  // Si non authentifié, rediriger vers login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Si authentifié, permettre l'accès
  return <Outlet />;
};

/**
 * Route garde pour les pages publiques mobile (Login/Register)
 * - Bloque l'accès desktop
 * - Redirige vers dashboard si déjà authentifié
 */
export const MobilePublicRoute: React.FC = () => {
  const { isMobile } = useDeviceDetection();
  const { user, loading } = useAuth();

  // Si on est sur desktop, rediriger vers la page d'accueil
  if (!isMobile) {
    return <Navigate to="/" replace />;
  }

  // Attendre le chargement de l'authentification (ne jamais retourner null)
  if (loading) {
    return <MobileLoadingScreen />;
  }

  // Si déjà authentifié, rediriger vers le dashboard approprié
  if (user) {
    const dashboardPath = user.role === Role.ADMIN 
      ? '/mobile/dashboard' 
      : '/mobile/dashboard';
    return <Navigate to={dashboardPath} replace />;
  }

  // Sinon, permettre l'accès (Login/Register)
  return <Outlet />;
};


